/**
 * Drag-reorder maths for the User settings list (#68).
 *
 * Each user_profiles row carries its own `sortOrder`, spaced 65536 apart like
 * work_items.position. A move rewrites only the row that moved, to the midpoint
 * of its new neighbours, so two owners reordering at once each write one row
 * and neither overwrites the other. When the gap has been halved too often to
 * split again, the whole list is renumbered — rare, and still one write per row.
 */

export const SORT_GAP = 65536;

/** Below this, a midpoint stops being distinguishable from its neighbours. */
const MIN_GAP = 1e-6;

/**
 * @param {Array<{id: string, sortOrder: number}>} rows  in their current display order
 * @param {string} activeId  the row being dragged
 * @param {string} overId    the row it was dropped on
 * @returns {Array<{id: string, sortOrder: number}>} the writes to make; empty for a no-op
 */
export function sortOrderUpdatesForMove(rows, activeId, overId) {
  const from = rows.findIndex((r) => r.id === activeId);
  const to = rows.findIndex((r) => r.id === overId);
  if (from === -1 || to === -1 || from === to) return [];

  const reordered = rows.slice();
  const [moved] = reordered.splice(from, 1);
  reordered.splice(to, 0, moved);

  const before = reordered[to - 1];
  const after = reordered[to + 1];
  let sortOrder;
  if (!before) sortOrder = Number(after.sortOrder) - SORT_GAP;
  else if (!after) sortOrder = Number(before.sortOrder) + SORT_GAP;
  else sortOrder = (Number(before.sortOrder) + Number(after.sortOrder)) / 2;

  const lower = before ? Number(before.sortOrder) : -Infinity;
  const upper = after ? Number(after.sortOrder) : Infinity;
  if (Number.isFinite(sortOrder) && sortOrder - lower > MIN_GAP && upper - sortOrder > MIN_GAP) {
    return [{ id: moved.id, sortOrder }];
  }

  // Out of room (or duplicate orders from a backfill race): renumber everything.
  return reordered
    .map((r, i) => ({ id: r.id, sortOrder: (i + 1) * SORT_GAP }))
    .filter((u, i) => Number(reordered[i].sortOrder) !== u.sortOrder);
}

/** Apply writes from sortOrderUpdatesForMove to a list and return it re-sorted. */
export function applySortOrders(rows, updates) {
  const byId = new Map(updates.map((u) => [u.id, u.sortOrder]));
  return rows
    .map((r) => (byId.has(r.id) ? { ...r, sortOrder: byId.get(r.id) } : r))
    .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
}
